import { useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import type React from 'react';

import type { CropperCanvas, CropperImage } from 'cropperjs';
import { debounce } from 'lodash-es';

import { useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import type { CropperInstance, MediaCropArea } from '#src/app/shared/hooks/imageUpload';

import {
	getCoverTranslateBounds,
	moveImageTo,
	naturalOffsetToTranslate,
	translateToNaturalOffset,
} from './coverTransform';
import './registerCropperElements';

// The cropper element needs to be the same size as the uploader. To accomplish this,
// the same styling is passed to it.
const uploaderStyle = {
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	position: 'absolute' as const,
};

const handleStyle = {
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	position: 'absolute' as const,
};

type CropperElementProps = {
	crop?: MediaCropArea;
	disabled?: boolean;
	image?: string;
	// 100 is cropperjs's own hardcoded floor for each dimension, so these defaults leave
	// consumers that don't pass them with the exact behavior they had before the props existed.
	minContainerHeight?: number;
	minContainerWidth?: number;
	updateCrop: (imageCrop: MediaCropArea) => void;
	updateImage?: (blob: Blob) => void;
	onInitialized?: (instance: CropperInstance) => void;
};

// oxlint-disable-next-line max-lines-per-function
const CropperElementInner: React.FC<CropperElementProps> = ({
	crop,
	disabled = false,
	image,
	minContainerHeight = 100,
	minContainerWidth = 100,
	updateCrop,
	updateImage,
	onInitialized,
}) => {
	const canvasRef = useRef<CropperCanvas | null>(null);
	const imageRef = useRef<CropperImage | null>(null);
	const { translate } = useI18n();
	// cropper-image only exposes the loaded image's natural size via the promise `$ready()`
	// resolves with, so it's captured here once for reuse in the (much more frequent) crop handler.
	const naturalSizeRef = useRef<{ width: number; height: number } | undefined>(undefined);
	// `$moveTo()` re-emits `transform` synchronously, which would otherwise re-enter
	// `clampToCover` recursively (observed to blow the call stack in production) before the
	// corrected value has a chance to read back as in-bounds.
	const clampingRef = useRef(false);

	const refForFocus = useCallback((node: HTMLDivElement | null) => {
		// this will trigger an autofocus on first render only
		node && node.focus();
	}, []);

	// Returns the current natural-image-to-displayed-image scale (cropper-image is drawn at its
	// natural size, then scaled down by its own CSS transform to cover the container).
	const getScale = useCallback(() => {
		const [scale] = imageRef.current?.$getTransform() ?? [];
		return typeof scale === 'number' && scale > 0 ? scale : undefined;
	}, [imageRef]);

	// Because of the fixed viewport, we set the initial crop by moving the image behind it.
	const setInitialPan = useCallback(() => {
		const imageElement = imageRef.current;
		const naturalSize = naturalSizeRef.current;
		const scale = getScale();
		if (crop && imageElement && naturalSize && scale) {
			moveImageTo(
				imageElement,
				naturalOffsetToTranslate(crop.x, naturalSize.width, scale),
				naturalOffsetToTranslate(crop.y, naturalSize.height, scale),
				scale,
			);
		}
	}, [crop, getScale, imageRef]);

	const moveCropper = useCallback(
		(event: KeyboardEvent<HTMLDivElement>, x: number, y: number) => {
			event.preventDefault();
			imageRef.current?.$move(x, y);
		},
		[imageRef],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (!disabled) {
				switch (event.key) {
					case 'ArrowLeft':
						moveCropper(event, -1, 0);
						break;
					case 'ArrowRight':
						moveCropper(event, 1, 0);
						break;
					case 'ArrowDown':
						moveCropper(event, 0, 1);
						break;
					case 'ArrowUp':
						moveCropper(event, 0, -1);
						break;
					default:
						break;
				}
			}
		},
		[disabled, moveCropper],
	);

	// cropperjs v2 doesn't constrain panning the way v1's `viewMode: 3` did, so a drag can leave
	// part of the fixed viewport uncovered (blank/black borders in the exported crop). Snap the
	// image back so it always fully covers the canvas.
	const clampToCover = useCallback(() => {
		if (clampingRef.current) return;

		const imageElement = imageRef.current;
		const canvas = canvasRef.current;
		const naturalSize = naturalSizeRef.current;
		const scale = getScale();
		if (!imageElement || !canvas || !naturalSize || !scale) return;

		const [, , , , translateX, translateY] = imageElement.$getTransform();
		const [minX, maxX] = getCoverTranslateBounds(naturalSize.width, scale, canvas.offsetWidth);
		const [minY, maxY] = getCoverTranslateBounds(naturalSize.height, scale, canvas.offsetHeight);
		const clampedX = Math.min(maxX, Math.max(minX, translateX));
		const clampedY = Math.min(maxY, Math.max(minY, translateY));

		if (clampedX !== translateX || clampedY !== translateY) {
			clampingRef.current = true;
			try {
				moveImageTo(imageElement, clampedX, clampedY, scale);
			} finally {
				clampingRef.current = false;
			}
		}
	}, [getScale]);

	// Renders the currently visible viewport at native image resolution (matching the source
	// photo's own pixel density, rather than however large the on-screen viewport happens to be).
	const getCroppedCanvas = useCallback(async () => {
		const canvas = canvasRef.current;
		const scale = getScale();
		if (!canvas || !scale) return undefined;
		return canvas.$toCanvas({
			width: canvas.offsetWidth / scale,
			height: canvas.offsetHeight / scale,
		});
	}, [canvasRef, getScale]);

	// TODO: investigate why `onCrop` still gets called after its unmounted, leading to a potential
	// memory leak
	// To ensure we save the desired repositioned image, we update Uppy with the new cropped image from Cropper.
	// Testing library's drag and drop is not supported by jsdom
	/* istanbul ignore next */
	// eslint-disable-next-line react-hooks/refs
	const onCrop = debounce(async () => {
		const imageElement = imageRef.current;
		const scale = getScale();

		const naturalSize = naturalSizeRef.current;

		if (!imageElement || !scale || !naturalSize) return;

		const [, , , , translateX, translateY] = imageElement.$getTransform();
		const { width: naturalWidth, height: naturalHeight } = naturalSize;

		const cropData: MediaCropArea = {
			x: translateToNaturalOffset(translateX, naturalWidth, scale),
			y: translateToNaturalOffset(translateY, naturalHeight, scale),
			w: naturalWidth,
			h: naturalHeight,
			croppedWidth: naturalWidth * scale,
			croppedHeight: naturalHeight * scale,
		};

		let croppedCanvas: HTMLCanvasElement | undefined;
		try {
			croppedCanvas = await getCroppedCanvas();
		} catch {
			// the element was disconnected from the DOM before the crop could be exported
			return;
		}

		if (!croppedCanvas) return;

		updateCrop({ ...cropData, newWidth: croppedCanvas.width, newHeight: croppedCanvas.height });

		try {
			croppedCanvas.toBlob((blob) => {
				if (blob && updateImage) updateImage(blob);
			}, 'image/jpeg');
		} catch {
			// canvas tainted by cross-origin image — crop cannot be exported
		}
	}, 250);

	// React doesn't wire up a plain `onTransform` JSX prop as a real `transform` event listener for
	// a custom element, so it's attached imperatively instead.
	useEffect(() => {
		const imageElement = imageRef.current;
		if (!imageElement) return undefined;
		const handleTransform = () => {
			clampToCover();
			void onCrop();
		};
		imageElement.addEventListener('transform', handleTransform);
		return () => imageElement.removeEventListener('transform', handleTransform);
	});

	useEffect(() => {
		const readyImage = async () => {
			try {
				const loadedImage = await imageRef.current?.$ready();
				if (!loadedImage) return;
				naturalSizeRef.current = { width: loadedImage.naturalWidth, height: loadedImage.naturalHeight };
				setInitialPan();
			} catch {
				// the image failed to load — nothing to position
			}
		};
		void readyImage();
		// only run once, when the image first becomes ready
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image]);

	useEffect(() => {
		if (onInitialized) {
			onInitialized({
				getCroppedImageDataUrl: async () => {
					const croppedCanvas = await getCroppedCanvas();
					return croppedCanvas?.toDataURL() ?? '';
				},
			});
		}
		// only run once, when the image first becomes ready
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image]);

	return (
		<div
			ref={refForFocus}
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
			role="button"
			data-testid="cropper-container"
			tabIndex={0}
			aria-label={translate('corgi.sap.media.a11y.cropper.crop_aria_label')}
			onKeyDown={handleKeyDown}
			className="-ml-px h-full p-px focus:outline-2 focus:outline-offset-1 focus:outline-[hsla(305,6%,22%,0.185)]"
		>
			<cropper-canvas
				ref={canvasRef}
				disabled={disabled}
				style={{ ...uploaderStyle, minWidth: minContainerWidth, minHeight: minContainerHeight }}
			>
				<cropper-image ref={imageRef} src={image} alt="" initial-fit="cover" translatable data-qa="cropper-image" />
				<cropper-handle action="move" plain style={handleStyle} />
			</cropper-canvas>
		</div>
	);
};

/** cropperjs's custom elements aren't supported server-side (see registerCropperElements.ts). */
export const CropperElement: React.FC<CropperElementProps> = (props) => (
	<ClientRender>
		<CropperElementInner {...props} />
	</ClientRender>
);
